import { LoadingOutlined, MessageOutlined, WarningTwoTone } from '@ant-design/icons';
import { Badge, Button, Empty, Popover, Typography, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { subscribeChat } from '../../../clients/chat/connection';
import useAgentChatStatus from '../../../hooks/agentStatus/useAgentChatStatus';
import useChatConnectionState from '../../../hooks/plugins/chat/useChatConnectionState';
import { usePluginStore } from '../../../store/RootStoreContext';
import { getTenantId } from '../../../utils/accountHelper';
import { PluginType } from '../types';
import ChatApp from './ChatApp';

// This component is responsible both to control chat app status and pass it to the child component, also to render loading indicator for undefined status
function ChatAppContainer() {
    const {
        token: { fontSizeIcon, zIndexBase }
    } = theme.useToken();
    const {
        data: { accessToken }
    } = useSession();
    const { t } = useTranslation('common');
    const agentChatStatus = useAgentChatStatus();

    const {
        activeChatEntities,
        chatPlugin,
        pluginsErrors: { CHAT: chatPluginErrors },
        pluginIsInitializing: { CHAT: chatPluginIsInitializing },
        setPluginIsInitializing
    } = usePluginStore();

    const pluginId = chatPlugin?.pluginId;
    const brokerUrl = chatPlugin?.manifest?.src;
    const pluginName = chatPlugin?.name;
    const isInitializing = chatPluginIsInitializing?.[pluginId] ?? true;
    const error = chatPluginErrors?.[pluginId] ?? null;

    const { connectedToChat } = useChatConnectionState();

    const chatAppStatus = error ?? (connectedToChat ? agentChatStatus : undefined);

    useEffect(() => {
        if (pluginId) {
            // to make AgentStatusDropdown to show loading indicator and to prevent user from changing AgentStatus
            setPluginIsInitializing(pluginId, PluginType.CHAT, !chatAppStatus);
        }
    }, [chatAppStatus, pluginId, setPluginIsInitializing]);

    useEffect(() => {
        if (chatAppStatus === 'AWAY' || (chatAppStatus === 'ONLINE' && brokerUrl)) {
            // ensure all subscriptions are updated/initialized properly
            activeChatEntities.forEach((chatEntity) => {
                subscribeChat({
                    brokerUrl,
                    chatSessionId: chatEntity.chatSessionId,
                    tenantId: getTenantId(),
                    token: accessToken
                });
            });
        }
    }, [accessToken, activeChatEntities, chatAppStatus, brokerUrl]);

    if (!chatPlugin) {
        return null;
    }

    if (!pluginId) {
        return (
            <Popover
                content={
                    <div
                        style={{
                            width: 200,
                            height: 300,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Empty
                            description={pluginName}
                            image={<MessageOutlined style={{ fontSize: 25 }} />}
                            imageStyle={{ width: '100%', height: '100%' }}
                        >
                            <Trans
                                ns="common"
                                i18nKey="plugin.chat.error"
                                values={{
                                    status: null,
                                    error: t('plugin.chat.invalidSettings')
                                }}
                            >
                                <strong>Error:</strong>
                                <span>status</span>
                                <p>error</p>
                                <p>message</p>
                            </Trans>
                        </Empty>
                    </div>
                }
                title={<Typography.Text strong>{t('plugin.chat.status.none')}</Typography.Text>}
                trigger="click"
                placement="bottomRight"
            >
                <Badge count={<WarningTwoTone twoToneColor="red" />} offset={[0, 30]}>
                    <Button type="default" icon={<MessageOutlined />} />
                </Badge>
            </Popover>
        );
    }

    if (!chatAppStatus || isInitializing)
        return (
            <div>
                <div style={{ position: 'relative' }}>
                    <LoadingOutlined
                        style={{
                            position: 'absolute',
                            right: -(fontSizeIcon * 0.5),
                            bottom: -(fontSizeIcon * 3),
                            zIndex: zIndexBase + 1
                        }}
                    />
                </div>
                <Button type="default" icon={<MessageOutlined />} disabled />
            </div>
        );

    return <ChatApp chatAppStatus={chatAppStatus} pluginId={pluginId} />;
}

export default observer(ChatAppContainer);
